<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import type { UserOutput } from '@ilteducation/customer-mgmt-types';
import { useEditUser } from '@ilteducation/customer-mgmt';
import { useUserIdentifiers } from '@ilteducation/user-identifiers';
import { MdiFaIcon } from '@ilteducation/components';
import { faEnvelope, faUser, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import Modal from '../Modal.vue';
import useModal, { ModalTypes } from '../../composables/useModal';
import { useLocale } from '../../composables/useLocale';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';
import useToast from '../../composables/useToast';
import { Role } from '../../types/user';
import { createErrorParser } from '../../error-utils';
import { checkAssociationUpdatePermission } from '@ilteducation/customer-mgmt-permissions';
import { usePermissions } from '../../composables/usePermissions';
import type { SlimOrganization } from '@ilteducation/my-account-types';

const { t } = useLocale();

type Props = {
  name: ModalTypes;
  user: UserOutput;
  organization: SlimOrganization;
  role: Role;
};

const props = defineProps<Props>();

type Emits = {
  useDifferentUsername: [];
  goToUserProfile: [userUid: string];
};
const emit = defineEmits<Emits>();

const { isOpen, close } = useModal(props.name);
const { upsertAssociations, error: upsertAssociationError } = useEditUser();
const { toast } = useToast();
const { availableIdentifiers } = useUserIdentifiers();

const isLoading = ref(false);

const userRoleSchoolInfo = computed(() => {
  const usersCurrentAssociation = props.user.associations.find((a) => a.schoolUnit.uid === props.organization.uid);
  if (usersCurrentAssociation) {
    return t(`lcp.role_in_school_highlighted`, {
      role: t(`lcp.role_${usersCurrentAssociation.role}`),
      school: usersCurrentAssociation.schoolUnit.name,
    });
  }

  const firstAssociation = props.user.associations[0] ?? {
    role: t(`lcp.role_student`),
    schoolUnit: { name: 'unknown' },
  };

  return t(`lcp.role_in_school_highlighted`, {
    role: t(`lcp.role_${firstAssociation.role}`),
    school: firstAssociation.schoolUnit.name,
  });
});

const otherAssociations = computed(() => {
  return props.user.associations
    .filter((assoc) => assoc.schoolUnit.uid !== props.organization.uid)
    .map((assoc) =>
      t(`lcp.role_in_school_highlighted`, { role: t(`lcp.role_${assoc.role}`), school: assoc.schoolUnit.name }),
    );
});

const lastActiveAt = computed(() => {
  if (!props.user.lastActiveAt) return null;
  return new Date(props.user.lastActiveAt).toLocaleDateString().replace(/(\/)/g, '-');
});

const parseError = createErrorParser(t);
const onAddExistingUser = async () => {
  if (!props.user) return;

  isLoading.value = true;
  try {
    const success = await upsertAssociations(props.user.uid, [{ schoolUid: props.organization.uid, role: props.role }]);
    if (success) {
      toast(t('lcp.user_added_to_organization'), 'success');
      emit('goToUserProfile', props.user.uid);
    } else {
      const toastMessage: string = parseError(upsertAssociationError.value) ?? t('lcp.errors.error_occurred');
      toast(toastMessage, 'error');
    }
  } catch (e) {
    console.error(e);
    toast(t('lcp.failed_to_add_user_to_organization'), 'error');
  } finally {
    isLoading.value = false;
  }
};

const goToProfile = () => {
  emit('goToUserProfile', props.user.uid);
};

const getIconByType = (type: string) => {
  const img = availableIdentifiers.value.find((e) => e.type === type)?.iconDataUri || 'mdi-window-restore';
  if (typeof img === 'string' && img.startsWith('data:image/svg+xml;utf8,')) {
    const base64svg = `data:image/svg+xml,${encodeURIComponent(img.replace('data:image/svg+xml;utf8,', ''))}`;
    return base64svg;
  }
  return img;
};

const { organization: currentOrganization } = useCurrentOrganization();
const { permissionContext, getCallerAssociation, reporterBoolean, createTargetAssociation } = usePermissions();
const allowedToAddUser = ref<boolean>(false);
watchEffect(async () => {
  const currentAssociation = props.user.associations.find(
    (assoc) => assoc.schoolUnit.uid === currentOrganization.value.uid,
  );
  if (!currentAssociation) {
    allowedToAddUser.value = true;
    return;
  }
  allowedToAddUser.value = await checkAssociationUpdatePermission(
    permissionContext.value,
    createTargetAssociation(props.user, currentAssociation),
    {
      role: props.role,
    },
    getCallerAssociation,
    reporterBoolean,
  );
});

const modalActions = computed(() => {
  if (allowedToAddUser.value) {
    return {
      'action-label': t('lcp.add_existing_user_to_org', { organization: props.organization.name }),
      action: onAddExistingUser,
    };
  }
  return {
    'action-label': t('lcp.go_to_user_profile'),
    action: goToProfile,
  };
});

const onUseDifferentUsername = () => {
  close();
  emit('useDifferentUsername');
};

const onClose = () => {
  close();
};
</script>

<template>
  <Modal
    :name="name"
    :title="t('lcp.username_already_in_use')"
    :is-open="isOpen"
    show-close-btn
    width="600"
    title-class="text-h5"
    :centerActions="true"
    :show-cancel-action="false"
    v-bind="modalActions"
    :secondary-action-label="t('lcp.use_different_username')"
    :secondary-action="onUseDifferentUsername"
    :loading="isLoading"
    @close="onClose"
  >
    <v-row>
      <v-col cols="12">
        <v-alert type="warning" variant="outlined" border="start" icon="mdi-alert">
          {{ t('lcp.user_with_username_found', { username: user.userName }) }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-if="user">
      <v-col cols="12">
        <v-sheet rounded="lg" color="surface-container" class="pa-4">
          <v-row no-gutters class="align-center">
            <v-col cols="auto" class="mr-2 d-flex">
              <MdiFaIcon size="small" :color="user.active ? 'success' : 'error'" :icon="faCircleCheck"></MdiFaIcon>
            </v-col>
            <v-col cols="auto" class="mr-2">
              <span class="font-weight-bold">{{ user.name }}</span>
            </v-col>
            <v-col cols="auto" v-if="lastActiveAt">
              <v-chip size="small" variant="outlined"> {{ t('lcp.last_active') }} {{ lastActiveAt }} </v-chip>
            </v-col>
          </v-row>

          <v-row no-gutters class="mb-2">
            <v-col cols="auto" class="mr-2">
              <span class="text-body-2 text-medium-emphasis" v-html="userRoleSchoolInfo"> </span>
            </v-col>
            <v-col v-if="otherAssociations.length" cols="auto" class="mr-2">
              <v-menu>
                <v-list>
                  <v-list-item v-for="(item, index) in otherAssociations" :key="index" :value="index">
                    <v-list-item-title><span v-html="item"></span></v-list-item-title>
                  </v-list-item>
                </v-list>

                <template v-slot:activator="{ props: activatorProps }">
                  <a v-bind="activatorProps" class="text-decoration-none text-primary">
                    {{ t(`lcp.n_more_organizations`, { count: otherAssociations.length }) }}
                  </a>
                </template>
              </v-menu>
            </v-col>
          </v-row>

          <v-row no-gutters class="mb-2" v-if="user.email">
            <v-col cols="auto" class="d-flex align-center">
              <MdiFaIcon :icon="faEnvelope" size="small" color="primary" class="mr-2"></MdiFaIcon>
              <span>{{ user.email }}</span>
            </v-col>
          </v-row>

          <v-row no-gutters class="mb-2">
            <v-col cols="auto" class="d-flex align-center">
              <MdiFaIcon :icon="faUser" size="small" color="primary" class="mr-2" />
              <span>{{ user.userName }}</span>
            </v-col>
          </v-row>

          <v-row no-gutters v-for="identifier in user.identifiers" :key="identifier.type">
            <v-col cols="auto" class="d-flex align-center">
              <v-img :src="getIconByType(identifier.type)" width="16" height="16" class="mr-2"></v-img>
              <span>{{ identifier.value }}</span>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <p class="text-body-1">
          {{ t('lcp.add_existing_user_question', { organization: organization.name }) }}
        </p>
      </v-col>
    </v-row>
  </Modal>
</template>
